/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1783296000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 06 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "reddit-ads",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Reddit",
          "depth": 0,
          "id": "connect-to-reddit"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Account ID",
          "depth": 1,
          "id": "account-id"
        }, {
          "value": "Syncing conversion events",
          "depth": 1,
          "id": "syncing-conversion-events"
        }, {
          "value": "Syncing custom audiences",
          "depth": 1,
          "id": "syncing-custom-audiences"
        }, {
          "value": "Validate your setup",
          "depth": 0,
          "id": "validate-your-setup"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Audience shows \"Invalid\" in Reddit Ads",
          "depth": 1,
          "id": "audience-shows-invalid-in-reddit-ads"
        }, {
          "value": "Don't see your data in Reddit?",
          "depth": 1,
          "id": "dont-see-your-data-in-reddit"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      code: "code",
      h3: "h3",
      img: "img",
      h4: "h4",
      ul: "ul",
      li: "li",
      ol: "ol"
    }, _provideComponents(), props.components), {EventSyncWarning, MatchBoosterDestination, ArrayFanOutTip, MatchRatesExplanation, PylonLink, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchBoosterDestination) _missingMdxReference("MatchBoosterDestination", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-conversion-events",
                children: _jsx(_components.strong, {
                  children: "Conversion events"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Reddit as conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://ads-api.reddit.com/docs/v3/operations/Post%20Conversion%20Events",
                children: "Conversions"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#syncing-custom-audiences",
                children: _jsx(_components.strong, {
                  children: "Audiences"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Reddit as a custom audience list"
            }), _jsx(_components.td, {
              children: "Upsert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://ads-api.reddit.com/docs/v3/operations/Create%20Custom%20Audience",
                children: "Audiences"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-reddit",
        children: "Connect to Reddit"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Reddit Ads"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Authorize Hightouch to access Reddit by logging into your account and agreeing to the app's usage. To successfully sync Custom Audiences, Hightouch requires the ", _jsx(_components.code, {
          children: "adsedit"
        }), " and ", _jsx(_components.code, {
          children: "adsread"
        }), " scope."]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsx(_components.h3, {
        id: "account-id",
        children: "Account ID"
      }), "\n", _jsx(_components.p, {
        children: "An Account ID is required to sync Conversion events and Audiences."
      }), "\n", _jsxs(_components.p, {
        children: ["By default, Hightouch will automatically use the Reddit Ads account owned by the user authenticated. If you'd like to use a different account ID, you can navigate to the respective dashboard and grab the ID from the URL.\n", _jsx(_components.img, {
          src: "destinations/destinations-reddit-ads-id.png",
          alt: ""
        })]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-conversion-events",
        children: "Syncing conversion events"
      }), "\n", _jsx(_components.h4, {
        id: "prerequisites",
        children: "Prerequisites"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Allowlisted by Reddit:"
          }), " Hightouch uses Reddit's Conversions API, which is in beta.\nTo send conversion events, your Reddit Ads account needs to by white listed by Reddit. Reach out to your Reddit account team to request access for your account to use the Conversions API."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Events within 7 days:"
          }), " If you are planning to set the event timestamp yourself, the event must occur within the last 7 days otherwise the conversion event will not go through."]
        }), "\n"]
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsxs(_components.p, {
        children: ["To view the schema and the field descriptions of the conversion events endpoint, visit the ", _jsx(_components.a, {
          href: "https://ads-api.reddit.com/docs/v3/operations/Post%20Conversion%20Events",
          children: "Reddit docs"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Notable fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Event Source URL"
          }), " (", _jsx(_components.code, {
            children: "event_source_url"
          }), "): the full URL of the web page where the event occurred, including query parameters. It applies to website events only. Reddit uses the URL to detect the event's domain in Events Manager. When the Reddit click ID (", _jsx(_components.code, {
            children: "rdt_cid"
          }), ") is appended as a query parameter, Reddit extracts it to improve attribution if the ", _jsx(_components.strong, {
            children: "Click ID"
          }), " field isn't mapped."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Products"
          }), " (", _jsx(_components.code, {
            children: "metadata[products]"
          }), "): an array of product objects that passes product-level information from your catalog; Reddit requires these fields when using product ads or dynamic product ads. Each product supports ", _jsx(_components.code, {
            children: "id"
          }), " (the variant SKU or GTIN matching your catalog, required by Reddit), ", _jsx(_components.code, {
            children: "name"
          }), ", ", _jsx(_components.code, {
            children: "category"
          }), ", ", _jsx(_components.code, {
            children: "quantity"
          }), ", and ", _jsx(_components.code, {
            children: "item_price"
          }), ". Per-product ", _jsx(_components.code, {
            children: "quantity"
          }), " and ", _jsx(_components.code, {
            children: "item_price"
          }), " aren't replacements for the event-level ", _jsx(_components.strong, {
            children: "Item Count"
          }), " and ", _jsx(_components.strong, {
            children: "Value"
          }), " fields, which Reddit still recommends sending on revenue-related events."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-custom-audiences",
        children: "Syncing custom audiences"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch will sync data to Reddit ads as a Custom Audience with the ", _jsx(_components.code, {
          children: "CUSTOMER_LIST"
        }), " type.\nYou can choose to create a new Custom Audience through Hightouch, or sync to an existing audience."]
      }), "\n", _jsx(_components.h4, {
        id: "user-identifiers",
        children: "User identifiers"
      }), "\n", _jsxs(_components.p, {
        children: ["Reddit accepts the following user fields: ", _jsx(_components.code, {
          children: "EMAIL_SHA256"
        }), " and ", _jsx(_components.code, {
          children: "MAID_SHA256"
        }), ". At least one identifier is required to successfully sync to Reddit ads."]
      }), "\n", _jsx(MatchBoosterDestination, {
        destinationName: "Reddit Ads"
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "Reddit Ads"
      }), "\n", _jsx(_components.h4, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsxs(_components.p, {
        children: ["The Reddit Custom Audiences API will ", _jsx(_components.strong, {
          children: "only"
        }), " accept hashed values. For audience syncs, by default, Hightouch automatically hashes first-party user data fields before sending requests to Reddit."]
      }), "\n", _jsx(_components.p, {
        children: "If the data in your model is already hashed, you can disable PII hashing to successfully send data to Reddit."
      }), "\n", _jsx(_components.h4, {
        id: "delete-behavior",
        children: "Delete behavior"
      }), "\n", _jsx(_components.p, {
        children: "When records leave your query result, they are removed from your audience by default. If you would not like to delete any records from your audience, you can choose to do nothing when records leave your query result."
      }), "\n", _jsx(_components.h2, {
        id: "validate-your-setup",
        children: "Validate your setup"
      }), "\n", _jsx(_components.p, {
        children: "After you configure your sync, run a test to confirm data flows correctly:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Start with a small test audience or a single record."
        }), "\n", _jsxs(_components.li, {
          children: ["Trigger a manual sync from the ", _jsx(_components.a, {
            href: "https://app.hightouch.com/syncs?from=docs",
            children: "Syncs overview"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["After the sync completes, verify the data in Reddit Ads:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["For audience syncs, go to ", _jsx(_components.strong, {
                children: "Audiences"
              }), " in the Reddit Ads dashboard and confirm your audience appears. Reddit may take 24–48 hours to fully process audience data."]
            }), "\n", _jsxs(_components.li, {
              children: ["For conversion event syncs, check the ", _jsx(_components.strong, {
                children: "Events Manager"
              }), " to confirm events were received."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsx(_components.li, {
          children: "If the sync shows errors, check the sync run log in Hightouch for specific API error messages."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsxs(_components.p, {
        children: ["Below only applies to the ", _jsx(_components.strong, {
          children: "Audiences"
        }), " sync type."]
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "Reddit Ads"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You may observe match rates in Reddit Ads that are greater than 100%. Reddit Ads returns the count of matched users as a range. Reddit Ads does not reliably deduplicate users and each of the IDs can be counted separately, meaning the calculated audience size can exceed the actual number of unique individuals. The use of an audience size range is meant to help advertisers understand whether an audience segment is broad enough for effective ad delivery, without misrepresenting the precision or uniqueness of the reach."
        })
      }), "\n", _jsx(_components.h3, {
        id: "audience-shows-invalid-in-reddit-ads",
        children: "Audience shows \"Invalid\" in Reddit Ads"
      }), "\n", _jsxs(_components.p, {
        children: ["After syncing an audience to Reddit, it may appear as \"Invalid\" in the Reddit Ads UI even though Hightouch reports a successful sync. This typically happens because Reddit is still processing the audience data. Wait 24–48 hours for processing to complete, then trigger a full resync in Hightouch. If the audience remains invalid after a full resync, ", _jsx(PylonLink, {
          pylonText: "My Reddit audience still shows Invalid after a full resync.",
          linkText: "contact support"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "dont-see-your-data-in-reddit",
        children: "Don't see your data in Reddit?"
      }), "\n", _jsxs(_components.p, {
        children: ["Make sure you've configured your sync to use the correct Reddit environment. You might've accidentally pointed to a different environment. If that doesn't solve your issue, ", _jsx(PylonLink, {
          pylonText: "",
          linkText: "please reach out"
        }), ". We're happy to help."]
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsxs("details", {
        children: [_jsx("summary", {
          children: _jsx(_components.strong, {
            children: "event_at timestamp must be less than 168h0m0s old"
          })
        }), _jsxs(_components.p, {
          children: ["Reddit Ads rejects conversion events where the ", _jsx(_components.code, {
            children: "event_at"
          }), " timestamp is older than 7 days (168 hours). If you see this error or a related ", _jsx(_components.code, {
            children: "RangeError: Invalid time value"
          }), ", ensure your model filters events to include only those from the last 7 days. You can add a ", _jsx(_components.code, {
            children: "WHERE"
          }), " clause like ", _jsx(_components.code, {
            children: "WHERE event_at >= DATEADD(day, -7, CURRENT_TIMESTAMP)"
          }), " to your model query."]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
